import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle, RefreshCcw, Home, ChevronDown, ChevronUp } from "lucide-react";
import { reportClientEvent } from "@/lib/telemetry";

const CLIENT_ROLES = ["client", "client_staff"];

// Entfernt (best effort) den roten CRA-Entwickler-Fehlerschirm, damit auf der Vorschau
// dieselbe freundliche Meldung wie in der Produktion erscheint. Wird nur aufgerufen,
// nachdem unsere Error Boundary den Fehler bereits sicher abgefangen hat.
function dismissDevOverlay() {
  try {
    document.querySelectorAll("body > iframe").forEach((f) => {
      const z = window.getComputedStyle(f).zIndex;
      if (z === "2147483647" || (parseInt(z, 10) || 0) > 2000000000) f.remove();
    });
  } catch (e) {
    /* egal */
  }
}

function Fallback({ isSuperAdmin, homePath, error, info }) {
  const [open, setOpen] = useState(false);
  const reload = () => window.location.reload();
  const goHome = () => window.location.assign(homePath || "/");
  return (
    <div
      data-testid="app-error-boundary"
      className="min-h-screen w-full flex items-center justify-center bg-[#0B0C10] text-white px-6 py-16"
    >
      <div className="w-full max-w-lg text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl" style={{ background: "rgba(163,230,53,0.10)" }}>
          <AlertTriangle className="h-8 w-8" style={{ color: "#A3E635" }} />
        </div>
        <h1 className="text-2xl font-bold tracking-tight mb-3">Es ist ein unerwarteter Fehler aufgetreten</h1>
        <p className="text-sm text-gray-400 leading-relaxed mb-8">
          Keine Sorge — Ihre Daten sind sicher gespeichert. Laden Sie die Seite bitte neu, meistens ist das Problem
          danach behoben. Falls es erneut auftritt, kontaktieren Sie uns bitte kurz.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            data-testid="error-reload-btn"
            onClick={reload}
            className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-black transition-transform hover:scale-[1.02]"
            style={{ background: "#A3E635" }}
          >
            <RefreshCcw className="h-4 w-4" /> Seite neu laden
          </button>
          <button
            data-testid="error-home-btn"
            onClick={goHome}
            className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-gray-200 border border-white/15 hover:bg-white/5 transition-colors"
          >
            <Home className="h-4 w-4" /> Zum Überblick
          </button>
        </div>

        {isSuperAdmin && (error || info) && (
          <div className="mt-10 text-left">
            <button
              data-testid="error-technical-toggle"
              onClick={() => setOpen((o) => !o)}
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/40 hover:text-white/70 transition-colors"
            >
              {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              Technische Details (nur Super Admin)
            </button>
            {open && (
              <pre
                data-testid="error-technical-details"
                className="mt-3 max-h-64 overflow-auto rounded-xl border border-white/10 bg-black/40 p-4 text-[11px] leading-relaxed text-red-300 whitespace-pre-wrap break-words"
              >
                {String(error?.message || error || "Unbekannter Fehler")}
                {info?.componentStack ? "\n" + info.componentStack : ""}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

class ErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    reportClientEvent({
      type: "client_error",
      message: (error && (error.message || String(error))) || "Unbekannter Render-Fehler",
      component: ((info && info.componentStack) || "").trim().split("\n")[0]?.trim() || "",
      path: typeof window !== "undefined" ? window.location.pathname : "",
    });
    // CRA-Overlay (nur Vorschau/Dev) best effort entfernen — die Boundary uebernimmt.
    dismissDevOverlay();
    [60, 300, 1000].forEach((ms) => setTimeout(dismissDevOverlay, ms));
  }

  componentDidUpdate(prevProps) {
    // Bei Navigationswechsel automatisch zuruecksetzen (z. B. Browser-Zurueck).
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null, info: null });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <Fallback
          isSuperAdmin={this.props.isSuperAdmin}
          homePath={this.props.homePath}
          error={this.state.error}
          info={this.state.info}
        />
      );
    }
    return this.props.children;
  }
}

// Funktionaler Wrapper: liefert Rolle (fuer Super-Admin-Details) + Home-Pfad + Reset-Key.
export default function AppErrorBoundary({ children }) {
  const location = useLocation();
  const auth = useAuth();
  const user = auth?.user || null;
  const isSuperAdmin = user?.role === "super_admin";
  const homePath = user ? (CLIENT_ROLES.includes(user.role) ? "/cockpit" : "/admin") : "/login";
  return (
    <ErrorBoundaryInner resetKey={location.pathname} isSuperAdmin={isSuperAdmin} homePath={homePath}>
      {children}
    </ErrorBoundaryInner>
  );
}
